// src/pages/LoginPage.jsx
import { useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import AgentBlaynaLogo from "../assets/Agent-Blayna-Logo.png";
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from "../config";
import { setAuth } from "../utils/auth";

const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  console.log("AgentBlayna API_BASE_URL =", API_BASE_URL);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const trimmedEmail = email.trim();

      const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err.detail || "Invalid email or password."
        );
      }

      const data = await res.json(); // { access_token, user? }

      // ---- Check if user previously completed onboarding (localStorage fallback) ----
      // This handles the case where backend doesn't persist onboarding status
      let onboardingCompleted = data.user?.onboarding_completed || false;

      if (!onboardingCompleted) {
        // Check localStorage for previously completed onboarding
        try {
          const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
          if (storedUser) {
            const parsed = JSON.parse(storedUser);
            // Only trust localStorage if it's the same user (by email)
            if (parsed.onboarding_completed && parsed.email === data.user?.email) {
              onboardingCompleted = true;
              // Merge the flag into the user data
              if (data.user) {
                data.user.onboarding_completed = true;
              }
            }
          }
        } catch (e) {
          // Ignore localStorage parse errors
        }
      }

      // ---- STORE TOKEN + USER using unified auth utility ----
      setAuth(data);

      // ---- Redirect based on role / onboarding status ----
      const dest = data.user?.role === "superadmin"
        ? "/admin"
        : !onboardingCompleted
          ? "/onboarding"
          : "/app";

      setTransitioning(true);
      setTimeout(() => {
        navigate(dest, { replace: true });
      }, 400);
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        padding: isMobile ? "16px 12px" : "24px 12px",
        overflow: "hidden",
      }}
    >
      {/* Back to landing page */}
      <div
        onClick={() => navigate("/")}
        style={{
          position: "absolute",
          top: isMobile ? 16 : 24,
          left: isMobile ? 16 : 24,
          zIndex: 10,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 14px",
          borderRadius: 999,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          border: "1px solid rgba(11, 15, 26, 0.10)",
          fontSize: 13,
          fontWeight: 600,
          color: "rgba(11, 15, 26, 0.65)",
          cursor: "pointer",
          transition: "background-color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
          e.currentTarget.style.color = "#0B0F1A";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.75)";
          e.currentTarget.style.color = "rgba(11, 15, 26, 0.65)";
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>&larr;</span>
        Back to home
      </div>

      {/* Multi-layer gradient background (matches landing page) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(1100px circle at 16% 0%, rgba(45, 91, 255, 0.15), transparent 55%),
            radial-gradient(1100px circle at 88% 12%, rgba(122, 92, 255, 0.10), transparent 60%),
            radial-gradient(1200px circle at 50% 110%, rgba(0, 0, 0, 0.06), transparent 60%),
            linear-gradient(180deg, #F7F8FA 0%, #F3F4F7 40%, #F7F8FA 100%)
          `,
        }}
      />
      {/* Subtle grain texture */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.05,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,0,0,.35) 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: isMobile ? "420px" : "980px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          borderRadius: isMobile ? "16px" : "24px",
          overflow: "hidden",
          boxShadow: `0 30px 110px rgba(11, 15, 26, 0.09), 0 4px 6px rgba(11, 15, 26, 0.02)`,
          backgroundColor: "rgba(255, 255, 255, 0.82)",
          backdropFilter: "blur(18px) saturate(180%)",
          WebkitBackdropFilter: "blur(18px) saturate(180%)",
          border: "1px solid rgba(11, 15, 26, 0.10)",
        }}
      >
        {/* LEFT: Hero / Value Prop - Hidden on mobile */}
        {!isMobile && (
        <div
          style={{
            flex: 1.1,
            padding: "32px 36px",
            borderRight: "1px solid rgba(11, 15, 26, 0.08)",
            background: "transparent",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            {/* Logo + tagline */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <img
                src={AgentBlaynaLogo}
                alt="AgentBlayna logo"
                style={{
                  height: 40,
                  width: "auto",
                  display: "block",
                }}
              />
              <div
                style={{
                  fontSize: 14.8,
                  fontWeight: 990,
                  letterSpacing: -0.3,
                  color: "#0B0F1A",
                }}
              >
                AgentBlayna
              </div>
            </div>

            <h1
              style={{
                fontSize: 32,
                lineHeight: 1.08,
                margin: "0 0 10px",
                fontWeight: 995,
                letterSpacing: "-1.5px",
                color: "#0B0F1A",
              }}
            >
              Smarter Real Estate Starts Here.
            </h1>

            <p
              style={{
                margin: "0 0 18px",
                fontSize: 15.5,
                color: "rgba(11, 15, 26, 0.68)",
                lineHeight: 1.8,
                maxWidth: 420,
              }}
            >
              AgentBlayna is your AI-powered CRM built for busy agents who can’t
              afford to let good leads slip away. It connects your clients,
              conversations, listings, and deals into one simple, focused
              workspace.
            </p>

            <div
              style={{
                display: "grid",
                gap: 10,
                fontSize: 14,
                color: "rgba(11, 15, 26, 0.72)",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "999px",
                    backgroundColor: "#e0f2fe",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    color: "#0369a1",
                    marginTop: 2,
                  }}
                >
                  ✓
                </span>
                <span>
                  <strong style={{ fontWeight: 800 }}>Never miss a money moment.</strong> AI surfaces hot
                  leads, cold follow-ups, and upcoming closings so you know who
                  to call first every day.
                </span>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "999px",
                    backgroundColor: "#eff6ff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    color: "#1d4ed8",
                    marginTop: 2,
                  }}
                >
                  ✓
                </span>
                <span>
                  <strong style={{ fontWeight: 800 }}>Personal touches on autopilot.</strong> Get proactive
                  alerts for birthdays, move-in anniversaries, and home-versary
                  dates with instant AI-drafted texts and emails.
                </span>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "999px",
                    backgroundColor: "#ecfdf5",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    color: "#15803d",
                    marginTop: 2,
                  }}
                >
                  ✓
                </span>
                <span>
                  <strong style={{ fontWeight: 800 }}>Know your pipeline at a glance.</strong> See volume,
                  commissions, and where every deal sits — so you can forecast
                  your next 90 days with confidence.
                </span>
              </div>
            </div>

            {/* <div
              style={{
                display: "inline-flex",
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid #dbeafe",
                backgroundColor: "#eff6ff",
                fontSize: 11,
                color: "#1d4ed8",
                fontWeight: 500,
                marginTop: 8,
              }}
            >
              ⚡ AI realtor copilot · Connect · Convert · Close
            </div> */}
          </div>

          <div
            style={{
              fontSize: 11,
              color: "rgba(11, 15, 26, 0.40)",
              marginTop: 24,
            }}
          >
            © {year} AgentBlayna · Smarter Real Estate Starts Here
          </div>
        </div>
        )}

        {/* RIGHT: Login Form */}
        <div
          style={{
            flex: isMobile ? 1 : 0.9,
            padding: isMobile ? "24px 20px" : "32px 36px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
          }}
        >
          {/* Mobile Logo Header */}
          {isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 24,
              }}
            >
              <img
                src={AgentBlaynaLogo}
                alt="AgentBlayna logo"
                style={{
                  height: 36,
                  width: "auto",
                  display: "block",
                }}
              />
              <div
                style={{
                  fontSize: 14.8,
                  fontWeight: 990,
                  letterSpacing: -0.3,
                  color: "#0B0F1A",
                }}
              >
                AgentBlayna
              </div>
            </div>
          )}

          <div
            style={{
              width: "100%",
              maxWidth: 360,
              padding: isMobile ? "20px 18px 24px" : "22px 22px 26px",
              borderRadius: isMobile ? 14 : 18,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: isMobile
                ? "0 8px 24px rgba(11, 15, 26, 0.06)"
                : `0 14px 32px rgba(11, 15, 26, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)`,
              border: "1px solid rgba(11, 15, 26, 0.10)",
            }}
          >
            <h2
              style={{
                fontSize: 20,
                margin: "0 0 6px",
                fontWeight: 900,
                letterSpacing: "-0.3px",
                color: "#0B0F1A",
              }}
            >
              Log in to AgentBlayna
            </h2>
            <p
              style={{
                margin: 0,
                marginBottom: 16,
                fontSize: 13.5,
                color: "rgba(11, 15, 26, 0.55)",
              }}
            >
              Enter your email and password to access your AgentBlayna workspace.
            </p>

            {error && (
              <div
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#b91c1c",
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 12,
                  marginBottom: 12,
                  border: "1px solid #fecaca",
                }}
              >
                {error}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              style={{ display: "grid", gap: 14, marginTop: 4 }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="email"
                  style={{
                    fontSize: 12.5,
                    color: "rgba(11, 15, 26, 0.60)",
                    fontWeight: 700,
                  }}
                >
                  Work email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="you@yourbrokerage.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid rgba(11, 15, 26, 0.10)",
                    fontSize: 14,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    color: "#0B0F1A",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="password"
                  style={{
                    fontSize: 12.5,
                    color: "rgba(11, 15, 26, 0.60)",
                    fontWeight: 700,
                  }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid rgba(11, 15, 26, 0.10)",
                    fontSize: 14,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    color: "#0B0F1A",
                    outline: "none",
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 6,
                  padding: "10px 12px",
                  borderRadius: 999,
                  background: "#0B0F1A",
                  color: "#ffffff",
                  border: "none",
                  fontSize: 15,
                  fontWeight: 900,
                  cursor: loading ? "default" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  boxShadow: "0 18px 50px rgba(11, 15, 26, 0.14)",
                  transition: "transform 0.08s ease, box-shadow 0.08s ease",
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = "translateY(1px)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(11, 15, 26, 0.12)";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 18px 50px rgba(11, 15, 26, 0.14)";
                }}
              >
                {loading ? "Signing you in…" : "Log In"}
              </button>
            </form>

            <div
              style={{
                marginTop: 18,
                fontSize: 12,
                color: "#9ca3af",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <span>
                Don't have an account?{" "}
                <span
                  onClick={() => navigate("/signup")}
                  style={{
                    color: "#2D5BFF",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  Sign up
                </span>
              </span>
            </div>
          </div>

          {/* Mobile Footer */}
          {isMobile && (
            <div
              style={{
                marginTop: 24,
                fontSize: 11,
                color: "rgba(11, 15, 26, 0.40)",
                textAlign: "center",
              }}
            >
              © {year} AgentBlayna · Smarter Real Estate
            </div>
          )}
        </div>
      </div>

      {/* Post-login transition overlay */}
      <AnimatePresence>
        {transitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: `
                radial-gradient(1100px circle at 16% 0%, rgba(45, 91, 255, 0.15), transparent 55%),
                radial-gradient(1100px circle at 88% 12%, rgba(122, 92, 255, 0.10), transparent 60%),
                linear-gradient(180deg, #F7F8FA 0%, #F3F4F7 40%, #F7F8FA 100%)
              `,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.img
              src={AgentBlaynaLogo}
              alt=""
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut" }}
              style={{ height: 48, width: "auto" }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginPage;
